import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetAttendanceReportListQuery,
  useGetTeamQuery,
  useGetallStudentQuery,
  useGetAttendanceSessionRosterQuery,
} from "../../../Api/attoDeskApi";
import { getAvatar } from "../../../utils/avatar";
import PremiumDatePicker from "../../../Components/Shared/DatePicker";
import PremiumSelect from "../../../Components/PremiumSelect";
import { AreaChart } from "../../../Components/InlineCharts";
import GradeSubjectsSummary from "../../AttendanceSession/GradeSubjectsSummary";
import GradeSubjectsDialog, { summarizeGradesCompact } from "../../AttendanceSession/GradeSubjectsDialog";
import { formatDateOnly, todayLocal } from "../../../utils/dateOnly";

type ReportType = "daily" | "weekly" | "monthly" | "custom";

interface StudentBreakdownRow {
  id: number;
  name: string;
  grade: string;
  avatar: string | null;
  total: number;
  present: number;
  absent: number;
  late: number;
  rate: number;
  recent_sessions: { date: string; status: string }[];
}

interface ReportRow {
  session_id: number;
  date: string;
  start_time?: string | null;
  end_time?: string | null;
  location?: string | null;
  mode?: string | null;
  student_count?: number;
  attendance_count?: number;
  enrolled_count?: number;
  grade_subjects?: Array<{
    grade_id: number;
    grade: { id: number; name: string; sort_order?: number | null };
    subjects: Array<{ id: number; name: string; sort_order?: number | null }>;
  }>;
  class_name: string;
  subjects: string;
  staff_name: string;
  staff_photo: string | null;
  grade: string;
  present: number;
  absent: number;
  late: number;
  total: number;
  attendance_rate: number;
  submitted: boolean;
}

interface ReportSummary {
  total_classes: number;
  total_present: number;
  total_absent: number;
  total_late: number;
  attendance_rate: number;
}

// Local-Y/M/D formatter — replaces the previous `d.toISOString().split("T")[0]`
// which returned UTC and could flip the date around midnight depending on the
// user's timezone. `formatDateOnly` reads local components so "today" always
// means today in the app's timezone (§22). `iso()` is kept for callers that
// were already local-safe (start/end of month constructed from local Date
// components) — those just get a stable output shape now.
const iso = (d: Date) => formatDateOnly(d);

const AttendanceReport: React.FC = () => {
  const navigate = useNavigate();
  // Default range is TODAY (per §1). Previously defaulted to the current
  // month, which combined with the backend's type-based override made the
  // report show data from the wrong window on first load. Type starts as
  // "custom" so the backend honours the explicit dates and never silently
  // recomputes a month range.
  const today = todayLocal();

  const [reportType, setReportType] = useState<ReportType>("custom");
  const [fromDate, setFromDate] = useState<string>(today);
  const [toDate, setToDate] = useState<string>(today);
  const [activeRange, setActiveRange] = useState<string>("today");
  const [staffFilter, setStaffFilter] = useState<string>("");
  const [gradeFilter, setGradeFilter] = useState<string>("");
  const [studentFilter, setStudentFilter] = useState<{ id: string; name: string }>({ id: "", name: "All Students" });
  const [studentSearch, setStudentSearch] = useState<string>("");
  // Session-roster drawer state — one dialog reused across all rows so
  // opening a second row swaps content without a remount flash.
  const [rosterSession, setRosterSession] = useState<null | { id: number; label: string }>(null);
  // "View Subjects" modal — shown for wide multi-grade mappings that can't
  // fit cleanly in the Class Details cell. Reuses the same shared dialog
  // already used by the Attendance list card.
  const [subjectsModal, setSubjectsModal] = useState<null | { groups: NonNullable<ReportRow["grade_subjects"]>; subtitle: string }>(null);

  const applyQuickRange = (r: string) => {
    setActiveRange(r);
    const now = new Date();
    if (r === "today") {
      const d = iso(now); setFromDate(d); setToDate(d); setReportType("daily");
    } else if (r === "week") {
      const day = now.getDay() || 7;
      const mon = new Date(now); mon.setDate(now.getDate() - (day - 1));
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      setFromDate(iso(mon)); setToDate(iso(sun)); setReportType("custom");
    } else if (r === "month") {
      setFromDate(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
      setToDate(iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
      setReportType("monthly");
    } else if (r === "year") {
      setFromDate(`${now.getFullYear()}-01-01`);
      setToDate(`${now.getFullYear()}-12-31`);
      setReportType("custom");
    }
  };

  // From > To is invalid — skip the request and surface a small hint on
  // the filter bar. Prevents wasted round-trips + prevents the backend
  // whereBetween from returning zero rows and looking like a "broken"
  // report (§17).
  const dateRangeInvalid = !!fromDate && !!toDate && fromDate > toDate;
  const args = useMemo(() => ({
    type: reportType,
    from: fromDate,
    to: toDate,
    staff_id: staffFilter,
    grade: gradeFilter,
    student_id: studentFilter.id,
  }), [reportType, fromDate, toDate, staffFilter, gradeFilter, studentFilter.id]);

  const { data, isFetching, isLoading, isError, error, refetch } = useGetAttendanceReportListQuery(
    args,
    { skip: dateRangeInvalid }
  );
  const payload: any = data?.data ?? {};
  const rows: ReportRow[] = payload.rows ?? [];
  const studentBreakdown: StudentBreakdownRow[] = payload.student_breakdown ?? [];
  const summary: ReportSummary = payload.summary ?? {
    total_classes: 0, total_present: 0, total_absent: 0, total_late: 0, attendance_rate: 0,
  };

  const { data: teamData } = useGetTeamQuery({ searchText: "" } as any);
  const staffList: { id: number; name: string }[] = ((teamData?.data as any[]) || []).map(
    (t: any) => {
      const f = t.teamFirstName ?? t.first_name ?? "";
      const l = t.teamLastName ?? t.last_name ?? "";
      const full = `${f} ${l}`.trim();
      return { id: t.id, name: full || t.name || `Staff #${t.id}` };
    }
  );

  // Student autocomplete — debounced 300ms so keystrokes don't hammer
  // the server. Empty search shows the first 25 by name. Selecting a
  // student narrows the whole report (KPIs + rows + heatmap).
  const [studentSearchDebounced, setStudentSearchDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setStudentSearchDebounced(studentSearch), 300);
    return () => clearTimeout(t);
  }, [studentSearch]);
  const { data: studentSearchData } = useGetallStudentQuery({
    searchText: studentSearchDebounced,
    id: "",
    page: 1,
    perPage: 25,
  } as any);
  const studentOptions: Array<{ id: string; name: string }> = useMemo(() => {
    const raw = (studentSearchData as any)?.data;
    if (!Array.isArray(raw)) return [];
    return raw.slice(0, 25).map((s: any) => ({
      id: String(s.id),
      name: [s.first_name, s.last_name].filter(Boolean).join(" ") || `Student #${s.id}`,
    }));
  }, [studentSearchData]);

  // Roster for the View Students drawer — only fires when the drawer is
  // open, so an unused table row costs zero requests.
  const { data: rosterData, isFetching: rosterFetching } = useGetAttendanceSessionRosterQuery(
    { sessionId: rosterSession?.id ?? 0 },
    { skip: !rosterSession }
  );
  const rosterStudents: Array<{ id: number; name: string; grade?: string | null; status: string; arrived_at?: string | null }> =
    ((rosterData?.data as any)?.students ?? []) as any;
  const rosterSessionInfo: any = (rosterData?.data as any)?.session ?? null;

  // Submission split — an unsubmitted session must never enter the
  // attendance-rate math. Rendering them as "0%" was the root cause of
  // the meaningless red-dot chart: 44 scheduled sessions with 0
  // submissions was being plotted as 44 zero points, implying every
  // student failed to attend when in fact attendance was never taken.
  const submittedRows = useMemo(() => rows.filter((r) => r.submitted), [rows]);
  const submittedCount = submittedRows.length;
  const totalCount = rows.length;
  const missingCount = Math.max(0, totalCount - submittedCount);
  const coveragePct = totalCount > 0 ? Math.round((submittedCount / totalCount) * 100) : 0;

  // Daily aggregation — one point per date, weighted by that day's
  // student-mark counts (matches the existing report-wide attendance-rate
  // rule: present + late over total marked). Only SUBMITTED sessions
  // contribute; days with zero submissions are omitted (§3, §16), so the
  // trend never draws a fake 0% gap where attendance simply wasn't taken.
  const dailyTrend = useMemo(() => {
    const byDate = new Map<string, { present: number; late: number; absent: number; total: number; sessions: number; submitted: number }>();
    rows.forEach((r) => {
      const cur = byDate.get(r.date) ?? { present: 0, late: 0, absent: 0, total: 0, sessions: 0, submitted: 0 };
      cur.sessions += 1;
      if (r.submitted) {
        cur.submitted += 1;
        cur.present += r.present;
        cur.late    += r.late;
        cur.absent  += r.absent;
        cur.total   += r.total;
      }
      byDate.set(r.date, cur);
    });
    const days = Array.from(byDate.entries())
      .filter(([, v]) => v.submitted > 0 && v.total > 0)
      .sort(([a], [b]) => a.localeCompare(b));
    let lastMonthKey = "";
    return days.map(([date, v]) => {
      const d = new Date(date + "T00:00:00");
      const monthKey = `${d.getFullYear()}-${d.getMonth()}`;
      const label = monthKey !== lastMonthKey
        ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
        : d.toLocaleDateString("en-US", { day: "numeric" });
      lastMonthKey = monthKey;
      const rate = Math.round(((v.present + v.late) / v.total) * 100);
      const long = d.toLocaleDateString("en-US", {
        weekday: "short", month: "short", day: "numeric", year: "numeric",
      });
      return {
        label,
        value: rate,
        extra: `${long} · ${v.submitted}/${v.sessions} sessions · P${v.present} L${v.late} A${v.absent}`,
      };
    });
  }, [rows]);

  // Single-day mode — a one-point line chart is pointless (§4). Below
  // we render a compact Session Overview instead.
  const singleDayMode = fromDate === toDate;
  const noSubmittedYet = submittedCount === 0;

  // Both leaderboards are gated on real submitted attendance for that
  // student. `s.total > 0` already means the student appeared in at least
  // one SUBMITTED session (the student_breakdown counts only
  // present/late/absent statuses server-side). Also expose a flag for the
  // "no submitted attendance anywhere" empty state so "At Risk Students"
  // stops proclaiming "All students above 75% — Great work!" when zero
  // attendance has actually been submitted (§13).
  const topStudents = useMemo(
    () => [...studentBreakdown].filter((s) => s.total > 0).sort((a, b) => b.rate - a.rate).slice(0, 3),
    [studentBreakdown]
  );
  const atRiskStudents = useMemo(
    () => [...studentBreakdown]
      .filter((s) => s.total > 0 && s.rate < 75)
      .sort((a, b) => a.rate - b.rate),
    [studentBreakdown]
  );
  const hasAnyStudentAttendance = useMemo(
    () => studentBreakdown.some((s) => s.total > 0),
    [studentBreakdown]
  );

  // Human-friendly period label. If from/to span exactly a single
  // calendar month, show "July 2026"; otherwise show "Aug 1 – Aug 31, 2026".
  const periodLabel = useMemo(() => {
    const f = new Date(fromDate);
    const t = new Date(toDate);
    const sameMonth = f.getFullYear() === t.getFullYear() && f.getMonth() === t.getMonth();
    const firstOfMonth = f.getDate() === 1;
    const lastOfMonth = t.getDate() === new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
    if (sameMonth && firstOfMonth && lastOfMonth) {
      return f.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    }
    return `${f.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${t.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  }, [fromDate, toDate]);

  const exportCSV = () => {
    if (!rows.length) return;
    // Columns realigned to match the new session-focused table (§20):
    // Date | Staff | Start | End | Location | Mode | Students | Present |
    // Absent | Late | Total | Rate. The legacy Subject/Grade columns are
    // dropped from the default export — they belong in the per-session
    // detail report, not the operational session summary.
    const header = [
      "Date","Staff","Start Time","End Time","Location","Mode",
      "Student Count","Present","Absent","Late","Total","Rate %",
    ];
    const q = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [header.join(",")]
      .concat(rows.map((r) => [
        r.date,
        q(r.staff_name),
        r.start_time ?? "",
        r.end_time ?? "",
        q(r.location ?? ""),
        q(r.mode ?? ""),
        r.student_count ?? r.total,
        r.present, r.absent, r.late, r.total, r.attendance_rate,
      ].join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-report-${fromDate}_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rateColor = (r: number) => (r >= 80 ? "#16a34a" : r >= 50 ? "#d97706" : "#dc2626");
  // New column layout (§11 preferred structure):
  //   DATE | STAFF | CLASS DETAILS | STUDENTS | PRESENT | ABSENT | LATE | TOTAL | RATE | ACTION
  const gridCols = "100px 200px 1.4fr 130px 70px 70px 60px 60px 100px 130px";
  const timeLabel = (t?: string | null) => {
    if (!t) return "";
    const [h, m] = String(t).split(":").map(Number);
    if (!Number.isFinite(h)) return String(t);
    const d = new Date(); d.setHours(h, m || 0, 0, 0);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };
  const statusChipStyle = (status: string) => {
    if (status === "present") return { bg: "#dcfce7", fg: "#166534", label: "Present" };
    if (status === "late")    return { bg: "#fef3c7", fg: "#92400e", label: "Late" };
    if (status === "absent")  return { bg: "#fee2e2", fg: "#991b1b", label: "Absent" };
    return { bg: "#f1f5f9", fg: "#475569", label: "Unmarked" };
  };

  return (
    <div style={{ background: "#f0f4f8", minHeight: "100vh", fontFamily: "Inter, sans-serif" }}>
      {/* ═══════════════ HERO HEADER ═══════════════ */}
      <div style={{
        background: "linear-gradient(135deg, #1b3a6b 0%, #2a5298 100%)",
        padding: "24px 32px",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 12,
        }}>
          <div>
            <div style={{
              color: "rgba(255,255,255,0.7)", fontSize: 12,
              letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 4,
            }}>Analytics</div>
            <h1 style={{ color: "#fff", fontSize: 26, fontWeight: 800, margin: 0 }}>
              📊 Attendance Reports
            </h1>
            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 13, marginTop: 4 }}>
              View and analyze attendance records by date, staff, and grade
            </div>
          </div>
          <button
            onClick={() => window.print()}
            style={{
              background: "#fff", color: "#1b3a6b", border: "none",
              borderRadius: 12, padding: "12px 22px", fontWeight: 800,
              fontSize: 14, cursor: "pointer",
              boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >🖨️ Print Report</button>
        </div>
      </div>

      {/* ═══════════════ 5 STAT CARDS ═══════════════ */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(5, 1fr)",
        gap: 16, margin: "24px 32px 0",
      }}>
        {[
          { label: "Total Classes",  value: summary.total_classes,  icon: "📚", color: "#1b3a6b", bg: "linear-gradient(135deg,#eef2ff,#e0e7ff)" },
          { label: "Total Present",  value: summary.total_present,  icon: "✅", color: "#16a34a", bg: "linear-gradient(135deg,#f0fdf4,#dcfce7)" },
          { label: "Total Absent",   value: summary.total_absent,   icon: "❌", color: "#dc2626", bg: "linear-gradient(135deg,#fef2f2,#fee2e2)" },
          { label: "Total Late",     value: summary.total_late,     icon: "🕐", color: "#d97706", bg: "linear-gradient(135deg,#fffbeb,#fef3c7)" },
          { label: "Avg Attendance", value: `${summary.attendance_rate || 0}%`, icon: "📊", color: "#7c3aed", bg: "linear-gradient(135deg,#f5f3ff,#ede9fe)" },
        ].map((c) => (
          <div key={c.label} style={{
            background: c.bg, borderRadius: 16, padding: "20px 22px",
            border: `1px solid ${c.color}22`,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            display: "flex", flexDirection: "column", alignItems: "flex-start",
          }}>
            <div style={{
              fontSize: 22, marginBottom: 8,
              background: "#fff", width: 40, height: 40, borderRadius: 10,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}>{c.icon}</div>
            <div style={{ fontSize: 32, fontWeight: 900, color: c.color, lineHeight: 1 }}>
              {c.value}
            </div>
            <div style={{
              fontSize: 10, fontWeight: 700, marginTop: 6, color: c.color,
              opacity: 0.75, letterSpacing: "0.6px", textTransform: "uppercase",
            }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* ═══════════════ ANALYTICS GRID: 65% / 35% ═══════════════ */}
      <div style={{
        display: "grid", gridTemplateColumns: "1.85fr 1fr",
        gap: 20, margin: "20px 32px 0",
      }}>
        {/* LEFT — Attendance trend + Student heatmap */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <ChartCard
            title={singleDayMode ? "📋 Session Attendance Overview" : "📈 Attendance Rate Trend"}
            subtitle={
              singleDayMode
                ? "One-day range — showing per-session status instead of a time-series line"
                : `Daily attendance rate for submitted sessions${dailyTrend.length ? " — 80% target line" : ""}`
            }
          >
            {noSubmittedYet ? (
              // §11 — replace the giant empty chart with a compact,
              // informative state so nobody mistakes "attendance not
              // taken" for "everybody was absent".
              <div style={{ padding: "24px 20px", textAlign: "center", color: "#64748b" }}>
                <div style={{ fontSize: 32, marginBottom: 6 }}>🕐</div>
                <div style={{ fontWeight: 700, color: "#1b3a6b", fontSize: 14, marginBottom: 4 }}>
                  No attendance submitted yet
                </div>
                <div style={{ fontSize: 12.5, color: "#64748b" }}>
                  {totalCount === 0
                    ? "There are no sessions in this range."
                    : `${totalCount} scheduled session${totalCount === 1 ? " is" : "s are"} awaiting attendance.`}
                </div>
              </div>
            ) : singleDayMode ? (
              // §4/§15 — replace one-point line chart with a ranked
              // list of that day's sessions. Unsubmitted ones surface
              // the "awaiting attendance" state per session rather
              // than as fake 0% dots.
              <SessionOverview rows={rows} />
            ) : dailyTrend.length > 0 ? (
              <AreaChart
                data={dailyTrend}
                height={220}
                color="#1b3a6b"
                target={80}
                targetLabel="Target: 80%"
              />
            ) : (
              <EmptyState label="No submitted attendance in this period" />
            )}
          </ChartCard>

          {/* Submission coverage — always shown so the difference between
              "no attendance" and "no sessions" is never ambiguous (§8). */}
          <div style={{
            background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "16px 20px",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#1b3a6b" }}>Attendance submission</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  {totalCount === 0
                    ? "No sessions in this range."
                    : `${submittedCount} of ${totalCount} session${totalCount === 1 ? "" : "s"} submitted${missingCount > 0 ? ` · ${missingCount} awaiting attendance` : ""}`}
                </div>
              </div>
              <div style={{ fontWeight: 800, fontSize: 20, color: coveragePct >= 80 ? "#16a34a" : coveragePct >= 50 ? "#d97706" : "#dc2626" }}>
                {totalCount === 0 ? "—" : `${coveragePct}%`}
              </div>
            </div>
            <div style={{ background: "#f1f5f9", borderRadius: 6, height: 8 }}>
              <div style={{
                width: `${coveragePct}%`, height: 8, borderRadius: 6,
                background: coveragePct >= 80 ? "#16a34a" : coveragePct >= 50 ? "#d97706" : "#dc2626",
                transition: "width 0.6s ease",
              }} />
            </div>
          </div>

          {/* Student Attendance card — compact empty state when nothing
              submitted, real heatmap otherwise (§10, §11). */}
          {hasAnyStudentAttendance ? (
            <ChartCard
              grow
              title="👥 Student Attendance Heatmap"
              subtitle="Each square = one class session"
              footer={
                <>
                  <div style={{ display: "flex", gap: 14, marginTop: 14, fontSize: 12, color: "#64748b" }}>
                    <HeatLegend color="#16a34a" label="Present" />
                    <HeatLegend color="#f59e0b" label="Late" />
                    <HeatLegend color="#ef4444" label="Absent" />
                    <HeatLegend color="#f1f5f9" label="No class" />
                  </div>
                  <div style={{
                    marginTop: 14, paddingTop: 12, borderTop: "1px solid #f1f5f9",
                    fontSize: 12, color: "#64748b",
                    display: "flex", gap: 20, flexWrap: "wrap",
                  }}>
                    <span>📅 {submittedCount} of {totalCount} session{totalCount === 1 ? "" : "s"} submitted</span>
                    <span>👥 {studentBreakdown.length} student{studentBreakdown.length === 1 ? "" : "s"}</span>
                    <span>📆 {periodLabel}</span>
                  </div>
                </>
              }
            >
              <Heatmap students={studentBreakdown} sessions={submittedRows} />
            </ChartCard>
          ) : (
            <div style={{
              background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px",
            }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#1b3a6b" }}>Student Attendance</div>
              <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 4 }}>
                {totalCount === 0
                  ? "No sessions in this range."
                  : `No attendance has been submitted for ${periodLabel}. Once attendance is submitted, student-level history will appear here.`}
              </div>
              {missingCount > 0 && (
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
                  {missingCount} scheduled session{missingCount === 1 ? "" : "s"} awaiting attendance.
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — Period Summary panel */}
        <div style={{
          background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          overflow: "hidden", display: "flex", flexDirection: "column",
        }}>
          <div style={{ background: "linear-gradient(135deg,#1b3a6b,#2a5298)", padding: "16px 20px" }}>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>📋 Period Summary</div>
            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, marginTop: 2 }}>
              {periodLabel}
            </div>
          </div>

          {/* Big rate + circle */}
          <div style={{ textAlign: "center", padding: "22px 20px 10px" }}>
            <div style={{
              width: 130, height: 130, borderRadius: "50%",
              margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center",
              background: noSubmittedYet
                ? "#f1f5f9"
                : `conic-gradient(${rateColor(summary.attendance_rate || 0)} ${(summary.attendance_rate || 0) * 3.6}deg, #f1f5f9 0)`,
              position: "relative",
            }}>
              <div style={{
                width: 106, height: 106, borderRadius: "50%",
                background: "#fff", display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
              }}>
                {noSubmittedYet ? (
                  <>
                    <div style={{ fontSize: 30, fontWeight: 900, lineHeight: 1, color: "#94a3b8" }}>—</div>
                    <div style={{ fontSize: 9.5, color: "#94a3b8", marginTop: 3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center", padding: "0 6px" }}>
                      No attendance submitted
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{
                      fontSize: 30, fontWeight: 900, lineHeight: 1,
                      color: rateColor(summary.attendance_rate || 0),
                    }}>{summary.attendance_rate || 0}%</div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Avg Rate
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Breakdown bars */}
          <div style={{ padding: "12px 20px 8px" }}>
            {(() => {
              const total = (summary.total_present || 0) + (summary.total_absent || 0) + (summary.total_late || 0) || 1;
              return ([
                { label: "Present", value: summary.total_present || 0, color: "#16a34a", icon: "✅" },
                { label: "Absent",  value: summary.total_absent  || 0, color: "#ef4444", icon: "❌" },
                { label: "Late",    value: summary.total_late    || 0, color: "#f59e0b", icon: "🕐" },
              ]).map((b) => {
                const pct = Math.round((b.value / total) * 100);
                return (
                  <div key={b.label} style={{ marginBottom: 10 }}>
                    <div style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      marginBottom: 4,
                    }}>
                      <span style={{
                        fontSize: 12, fontWeight: 600, color: "#64748b",
                        display: "flex", alignItems: "center", gap: 5,
                      }}>{b.icon} {b.label}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: b.color }}>
                        {b.value} <span style={{ color: "#94a3b8", fontWeight: 400 }}>({pct}%)</span>
                      </span>
                    </div>
                    <div style={{ background: "#f1f5f9", borderRadius: 6, height: 7 }}>
                      <div style={{
                        width: `${pct}%`, height: 7, borderRadius: 6,
                        background: b.color, transition: "width 0.6s ease",
                      }} />
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          {/* TOP ATTENDANCE */}
          <SectionDivider />
          <PanelSection title="🏆 Top Attendance">
            {topStudents.length === 0 ? (
              <MutedRow>
                {hasAnyStudentAttendance ? "No data" : "Available after attendance is submitted."}
              </MutedRow>
            ) : topStudents.map((s, i) => {
              const medal = i === 0 ? "#ffd700" : i === 1 ? "#c0c0c0" : "#cd7f32";
              return (
                <div key={s.id} style={rowStyle}>
                  <div style={{
                    width: 22, height: 22, borderRadius: "50%",
                    background: medal, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 800, flexShrink: 0,
                  }}>{i + 1}</div>
                  <img
                    src={getAvatar(s.name, s.avatar, 32)}
                    alt=""
                    style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "2px solid #bbf7d0" }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
                    <div style={{ fontSize: 10, color: "#94a3b8" }}>{s.present}/{s.total} sessions</div>
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 15, color: "#16a34a" }}>{s.rate}%</div>
                </div>
              );
            })}
          </PanelSection>

          {/* AT RISK — students with rate < 75% */}
          <SectionDivider />
          <PanelSection title="📉 At Risk Students">
            {atRiskStudents.length === 0 ? (
              !hasAnyStudentAttendance ? (
                // §13 — no submitted attendance means we CANNOT claim
                // every student is above 75%. Neutral state instead.
                <div style={{
                  background: "#f8fafc", border: "1px solid #e2e8f0",
                  borderRadius: 10, padding: "10px 12px",
                  color: "#64748b", fontSize: 12, fontWeight: 600,
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  ⏳ Not enough attendance data yet.
                </div>
              ) : (
                <div style={{
                  background: "#f0fdf4", border: "1px solid #bbf7d0",
                  borderRadius: 10, padding: "10px 12px",
                  color: "#16a34a", fontSize: 12, fontWeight: 600,
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  ✅ All students above 75% — Great work!
                </div>
              )
            ) : atRiskStudents.map((s) => (
              <div key={s.id} style={rowStyle}>
                <img
                  src={getAvatar(s.name, s.avatar, 32)}
                  alt=""
                  style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "2px solid #fecaca" }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
                  <div style={{ fontSize: 10, color: "#94a3b8" }}>
                    Missed {s.absent + s.late} of {s.total} classes
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontWeight: 800, fontSize: 15, color: s.rate < 50 ? "#dc2626" : "#d97706" }}>
                    {s.rate}%
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/student-history/${s.id}`); }}
                    title="Email parent"
                    style={{
                      background: "#eff6ff", color: "#2563eb",
                      border: "1px solid #bfdbfe", borderRadius: 7,
                      padding: "4px 8px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >📧 Email Parent</button>
                </div>
              </div>
            ))}
          </PanelSection>
        </div>
      </div>

      {/* ═══════════════ FILTER TOOLBAR ═══════════════
          Same controls that used to sit inside the hero, moved here so
          they read as "filters for the table below" rather than part of
          the analytics header. Nothing about the controls, state, or
          server-side filtering behaviour changes — layout only. */}
      <div style={{
        margin: "20px 32px 12px", background: "#fff", borderRadius: 14,
        padding: "12px 16px",
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            fontSize: 11, fontWeight: 700, color: "#94a3b8",
            textTransform: "uppercase", letterSpacing: "0.5px",
          }}>From</span>
          <PremiumDatePicker
            value={fromDate}
            onChange={(v) => { setFromDate(v); setActiveRange(""); setReportType("custom"); }}
            placeholder="Select"
          />
        </div>
        <span style={{ color: "#94a3b8", fontWeight: 700, fontSize: 16 }}>→</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            fontSize: 11, fontWeight: 700, color: "#94a3b8",
            textTransform: "uppercase", letterSpacing: "0.5px",
          }}>To</span>
          <PremiumDatePicker
            value={toDate}
            onChange={(v) => { setToDate(v); setActiveRange(""); setReportType("custom"); }}
            placeholder="Select"
          />
        </div>
        <div style={{ width: 1, height: 28, background: "#e2e8f0" }} />
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { label: "Today", range: "today" },
            { label: "Week",  range: "week" },
            { label: "Month", range: "month" },
            { label: "Year",  range: "year" },
          ].map((r) => (
            <button
              key={r.range}
              onClick={() => applyQuickRange(r.range)}
              style={{
                padding: "7px 14px", borderRadius: 8, border: "none",
                fontWeight: 600, fontSize: 12, cursor: "pointer",
                background: activeRange === r.range ? "#1b3a6b" : "#f1f5f9",
                color: activeRange === r.range ? "#fff" : "#64748b",
                transition: "all 0.15s",
              }}
            >{r.label}</button>
          ))}
        </div>
        <div style={{ width: 1, height: 28, background: "#e2e8f0" }} />
        <PremiumSelect
          value={staffFilter}
          onChange={setStaffFilter}
          placeholder="All Staff"
          emptyLabel="All Staff"
          showAvatar
          options={staffList.map((s) => ({ value: String(s.id), label: s.name }))}
          minWidth={150}
        />
        <PremiumSelect
          value={gradeFilter}
          onChange={setGradeFilter}
          placeholder="All Grades"
          emptyLabel="All Grades"
          options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((g) => ({
            value: `Grade ${g}`, label: `Grade ${g}`,
          }))}
          minWidth={130}
        />
        <PremiumSelect
          value={studentFilter.id}
          onChange={(val) => {
            const found = studentOptions.find((o) => o.id === val);
            setStudentFilter(val ? { id: val, name: found?.name || "" } : { id: "", name: "All Students" });
          }}
          onSearch={setStudentSearch}
          placeholder="All Students"
          emptyLabel="All Students"
          options={studentOptions.map((s) => ({ value: s.id, label: s.name }))}
          minWidth={170}
        />
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          {/* Subtle background-refresh hint — replaces the old table
              blanking behaviour so the admin still knows a fetch is
              in flight without losing the current results. */}
          {isFetching && rows.length > 0 && (
            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>
              ⏳ Refreshing…
            </span>
          )}
          {dateRangeInvalid && (
            <span style={{ fontSize: 11, color: "#b91c1c", fontWeight: 600 }}>
              ⚠ From date cannot be after To date
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching || dateRangeInvalid}
            style={{
              background: "#1b3a6b", color: "#fff", border: "none",
              borderRadius: 10, padding: "9px 22px",
              fontWeight: 700, fontSize: 13, cursor: "pointer",
              boxShadow: "0 4px 12px rgba(27,58,107,0.3)",
            }}
          >🔍 Generate</button>
          <button
            onClick={exportCSV}
            disabled={rows.length === 0}
            style={{
              background: rows.length === 0 ? "#f1f5f9" : "#f0fdf4",
              color: rows.length === 0 ? "#94a3b8" : "#16a34a",
              border: `1px solid ${rows.length === 0 ? "#e2e8f0" : "#bbf7d0"}`,
              borderRadius: 10, padding: "9px 18px", fontWeight: 600,
              fontSize: 13, cursor: rows.length === 0 ? "not-allowed" : "pointer",
            }}
          >⬇️ CSV</button>
        </div>
      </div>

      {/* ═══════════════ SESSION TABLE ═══════════════ */}
      <div style={{
        margin: "0 32px 32px", background: "#fff", borderRadius: 16,
        border: "1px solid #e2e8f0", overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        <div style={{
          background: "#1b3a6b", display: "grid",
          gridTemplateColumns: gridCols, padding: "12px 20px", gap: 12,
        }}>
          {["Date", "Staff", "Class Details", "Students", "Present", "Absent", "Late", "Total", "Rate", "Action"].map((h) => (
            <div key={h} style={{
              fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.85)",
              letterSpacing: "0.6px", textTransform: "uppercase",
            }}>{h}</div>
          ))}
        </div>

        {/* Table body states (§9):
              FIRST LOAD  — no cached data yet → skeleton loader
              ERROR       — explicit error state, never a silent loader
              EMPTY       — request settled with zero rows
              LOADED      — render rows; a subsequent background refresh
                            keeps rows visible (the tiny "Refreshing…"
                            hint next to Generate signals the in-flight
                            request without blanking the table).
            The old `isFetching`-only gate blanked the table on every
            refetch (Generate click, tag invalidation) which is why the
            report appeared to hang on "Loading report…" even after the
            response had already landed and populated the summary. */}
        {isLoading && rows.length === 0 ? (
          <div style={{ padding: "60px 20px", textAlign: "center", color: "#94a3b8" }}>Loading report…</div>
        ) : isError && rows.length === 0 ? (
          <div style={{ padding: "60px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontWeight: 600, color: "#b91c1c", fontSize: 16 }}>Unable to load attendance report</div>
            <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 6 }}>
              {(error as any)?.data?.message || "Please try again in a moment."}
            </div>
            <button
              onClick={() => refetch()}
              style={{
                marginTop: 14, background: "#1b3a6b", color: "#fff",
                border: "none", borderRadius: 10, padding: "8px 18px",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}
            >Retry</button>
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: "60px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            <div style={{ fontWeight: 600, color: "#1b3a6b", fontSize: 16 }}>No attendance records found</div>
            <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 6 }}>
              Try widening the date range or adjusting the filters.
            </div>
          </div>
        ) : rows.map((row, i) => {
          const students = row.student_count ?? row.total;
          const timeStr = row.start_time && row.end_time
            ? `${timeLabel(row.start_time)} – ${timeLabel(row.end_time)}`
            : "";
          const locMode = [row.location, row.mode].filter(Boolean).join(" · ");
          const secondary = [timeStr, locMode].filter(Boolean).join(" · ");
          const gs = Array.isArray(row.grade_subjects) ? row.grade_subjects : [];
          const totalMappings = gs.reduce((n, g) => n + g.subjects.length, 0);
          // Collapse rule (§13): when a class has more than 3 mappings OR
          // more than 3 grades, render a compact summary + View Subjects.
          const collapse = gs.length > 3 || totalMappings > 3;
          // Attendance Report is a HISTORICAL report screen — always
          // navigate to the detail/print report, never to the operational
          // Mark Attendance page. Marking happens under Academics →
          // Classes → Attendance; this page is view/analyze/print only.
          // The detail page itself shows "no attendance submitted" for a
          // historical session that was never marked.
          const goToAttendance = () => {
            navigate(`/attendance/report/${row.session_id}`);
          };
          return (
          <div
            key={row.session_id}
            onClick={goToAttendance}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f4ff")}
            onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#fafafa")}
            style={{
              display: "grid", gridTemplateColumns: gridCols,
              padding: "14px 20px", gap: 12, alignItems: "center",
              borderBottom: "1px solid #f1f5f9",
              background: i % 2 === 0 ? "#fff" : "#fafafa",
              cursor: "pointer", transition: "background 0.1s",
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: "#1b3a6b" }}>
              {new Date(row.date).toLocaleDateString("en-US", {
                month: "short", day: "numeric", year: "numeric",
              })}
            </div>
            {/* STAFF — first meaningful column */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <img
                src={getAvatar(row.staff_name, row.staff_photo, 28)}
                alt=""
                style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", border: "1.5px solid #e2e8f0", flexShrink: 0 }}
              />
              <span style={{
                fontSize: 12, fontWeight: 600, color: "#1a1a2e",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>{row.staff_name}</span>
            </div>
            {/* CLASS DETAILS — WHAT the staff taught, grouped by grade
                (§2–§4). Time/location are secondary metadata on a smaller
                line below. Falls back to class_name only when no
                grade-subject pairs are configured. */}
            <div style={{ minWidth: 0 }}>
              {gs.length > 0 ? (
                collapse ? (() => {
                  const { gradesLabel, mappingsCount } = summarizeGradesCompact(gs);
                  return (
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1a2e", lineHeight: 1.3 }}>
                        {gradesLabel}
                      </div>
                      <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2, display: "flex", alignItems: "center", gap: 6 }}>
                        <span>{mappingsCount} subject mapping{mappingsCount === 1 ? "" : "s"}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSubjectsModal({ groups: gs, subtitle: [row.staff_name, timeStr].filter(Boolean).join(" · ") });
                          }}
                          style={{
                            background: "#eff6ff", color: "#1b3a6b",
                            border: "1px solid #dbeafe", borderRadius: 6,
                            padding: "1px 8px", fontSize: 11, fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >View Subjects</button>
                      </div>
                    </div>
                  );
                })() : (
                  <div>
                    <GradeSubjectsSummary groups={gs} variant="light" compact />
                  </div>
                )
              ) : (
                // Only reached when structured grade_subjects is genuinely
                // empty (no configured pairs). Never regress to the flat
                // "English and Math, English and Science, Science, ..."
                // soup that upstream sometimes packs into class_name —
                // detect the giveaway (contains ", " or " and ") and
                // show a neutral placeholder instead so the historical
                // report never displays that grade-detached string.
                (() => {
                  const raw = String(row.class_name || "").trim();
                  const looksLikeFlatSoup = raw.includes(", ") || / and /i.test(raw);
                  return (
                    <div style={{ fontWeight: 600, fontSize: 13, color: looksLikeFlatSoup ? "#94a3b8" : "#1a1a2e" }}>
                      {looksLikeFlatSoup ? "No grade-subject mapping" : (raw || "Class")}
                    </div>
                  );
                })()
              )}
              {secondary && (
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{secondary}</div>
              )}
            </div>
            {/* STUDENTS — 👥 N · View Students opens the historical roster */}
            <div>
              <button
                onClick={(e) => { e.stopPropagation(); setRosterSession({ id: row.session_id, label: `${row.staff_name} · ${new Date(row.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}${timeStr ? ` · ${timeStr}` : ""}` }); }}
                disabled={students === 0}
                style={{
                  background: students === 0 ? "#f8fafc" : "#eff6ff",
                  color: students === 0 ? "#94a3b8" : "#1b3a6b",
                  border: `1px solid ${students === 0 ? "#e2e8f0" : "#dbeafe"}`,
                  borderRadius: 8, padding: "5px 10px",
                  fontSize: 12, fontWeight: 700,
                  cursor: students === 0 ? "not-allowed" : "pointer",
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
                title="View students in this session"
              >
                👥 {students} {students === 0 ? "" : (students === 1 ? "· View" : "· View")}
              </button>
            </div>
            <div style={{ fontWeight: 700, color: "#16a34a", fontSize: 15 }}>{row.present}</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: row.absent > 0 ? "#dc2626" : "#94a3b8" }}>{row.absent}</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: row.late > 0 ? "#d97706" : "#94a3b8" }}>{row.late}</div>
            <div style={{ fontWeight: 600, color: "#1a1a2e", fontSize: 14 }}>{row.total}</div>
            <div>
              <div style={{
                fontWeight: 800, fontSize: 15,
                color: row.total === 0 ? "#94a3b8" : rateColor(row.attendance_rate),
                marginBottom: 4,
              }}>
                {row.total === 0 ? "—" : `${row.attendance_rate}%`}
              </div>
              {row.total > 0 && (
                <div style={{ background: "#e2e8f0", borderRadius: 3, height: 5 }}>
                  <div style={{
                    width: `${row.attendance_rate}%`, height: 5, borderRadius: 3,
                    background: rateColor(row.attendance_rate),
                  }} />
                </div>
              )}
            </div>
            {/* ACTION — Attendance Report is a historical view/print
                surface, so this button is always "View Attendance". A
                session that was never marked still opens the detail
                page, which itself surfaces the "no attendance
                submitted" state. Marking lives on Classes → Attendance;
                do not offer it here (§1). Both buttons stopPropagation
                so they don't double-fire the row-level click (§15). */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
              {!row.submitted && (
                <span
                  title="No attendance was submitted for this session"
                  style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                    color: "#92400e", background: "#fef3c7",
                    border: "1px solid #fde68a", borderRadius: 6,
                    padding: "1px 6px", textTransform: "uppercase",
                  }}
                >Not Submitted</span>
              )}
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button
                  onClick={(e) => { e.stopPropagation(); goToAttendance(); }}
                  title="View attendance report for this session"
                  style={{
                    background: "#eff6ff", color: "#1b3a6b",
                    border: "1px solid #dbeafe",
                    borderRadius: 8, padding: "5px 10px", cursor: "pointer",
                    fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
                  }}
                >👁️ View</button>
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/attendance/report/${row.session_id}`); }}
                  title="Print attendance report for this session"
                  style={{
                    background: "#fff", border: "1px solid #cbd5e1",
                    borderRadius: 8, padding: "5px 8px", cursor: "pointer",
                    fontSize: 13, lineHeight: 1,
                  }}
                >🖨️</button>
              </div>
            </div>
          </div>
        );
        })}

        {rows.length > 0 && (
          <div style={{
            padding: "12px 20px", background: "#f8fafc",
            borderTop: "2px solid #e2e8f0",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              {rows.length} session{rows.length !== 1 ? "s" : ""} · Click any row for detail
            </span>
            <span style={{ fontSize: 12, color: "#94a3b8" }}>
              👆 Click a row to see full attendance breakdown
            </span>
          </div>
        )}
      </div>

      <GradeSubjectsDialog
        open={!!subjectsModal}
        onClose={() => setSubjectsModal(null)}
        groups={subjectsModal?.groups ?? []}
        subtitle={subjectsModal?.subtitle}
      />

      {/* View-Students drawer — historical roster for the clicked session.
          One dialog reused across every row; opening a new row swaps
          content without a remount flash. Fetches only when open. */}
      {rosterSession && (
        <div
          onClick={() => setRosterSession(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
            zIndex: 300, display: "flex", justifyContent: "flex-end",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(440px, 100%)", background: "#fff",
              boxShadow: "-8px 0 32px rgba(0,0,0,0.25)",
              display: "flex", flexDirection: "column",
              fontFamily: "Inter, sans-serif",
            }}
          >
            <div style={{ padding: "18px 22px", background: "linear-gradient(135deg,#1b3a6b,#2a5298)", color: "#fff" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800 }}>Students</div>
                  <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{rosterSession.label}</div>
                </div>
                <button
                  onClick={() => setRosterSession(null)}
                  style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 16 }}
                >×</button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
              {rosterFetching ? (
                <div style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>Loading students…</div>
              ) : rosterStudents.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>
                  No attendance recorded for this session.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {rosterStudents.map((s) => {
                    const chip = statusChipStyle(s.status);
                    return (
                      <div
                        key={s.id}
                        onClick={() => navigate(`/student-history/${s.id}`)}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "10px 12px", borderRadius: 10,
                          border: "1px solid #e2e8f0", background: "#fff",
                          cursor: "pointer", transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                      >
                        <img
                          src={getAvatar(s.name, (s as any).image ?? null, 32)}
                          alt=""
                          style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {s.name}
                          </div>
                          {s.grade && (
                            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>Grade {s.grade}</div>
                          )}
                        </div>
                        <span style={{
                          background: chip.bg, color: chip.fg,
                          borderRadius: 8, padding: "3px 10px",
                          fontSize: 11, fontWeight: 700,
                        }}>{chip.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {rosterSessionInfo && (
              <div style={{ padding: 12, borderTop: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", justifyContent: "space-between" }}>
                <button
                  onClick={() => setRosterSession(null)}
                  style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                >Close</button>
                <button
                  onClick={() => navigate(`/attendance/report/${rosterSession.id}`)}
                  style={{ background: "#1b3a6b", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                >🖨️ Open full report</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────── shared subcomponents ─────────────── //

const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
  grow?: boolean;
}> = ({ title, subtitle, footer, children, grow }) => (
  <div style={{
    background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0",
    padding: grow ? "24px 28px" : 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    flex: grow ? 1 : undefined,
    display: "flex", flexDirection: "column",
  }}>
    <div style={{ fontWeight: 700, fontSize: 15, color: "#1b3a6b", marginBottom: 4 }}>
      {title}
    </div>
    {subtitle && (
      <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>{subtitle}</div>
    )}
    <div style={{ flex: grow ? 1 : undefined }}>{children}</div>
    {footer}
  </div>
);

// Compact session-list card used inside the trend chart slot when the
// report range covers a single day. A one-point time series is
// visually pointless (§4/§15) — a ranked session view answers the
// same "how did today go?" question better.
const SessionOverview: React.FC<{ rows: ReportRow[] }> = ({ rows }) => {
  const sorted = [...rows].sort((a, b) => {
    const at = String(a.start_time || "");
    const bt = String(b.start_time || "");
    if (at !== bt) return at.localeCompare(bt);
    return (a.staff_name || "").localeCompare(b.staff_name || "");
  });
  const displayed = sorted.slice(0, 12);
  const timeLabel = (t?: string | null) => {
    if (!t) return "";
    const [h, m] = String(t).split(":").map(Number);
    if (!Number.isFinite(h)) return String(t);
    const d = new Date(); d.setHours(h, m || 0, 0, 0);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };
  const rateColor = (r: number) => (r >= 80 ? "#16a34a" : r >= 50 ? "#d97706" : "#dc2626");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {displayed.map((r) => (
        <div key={r.session_id} style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "8px 10px", borderRadius: 10, border: "1px solid #f1f5f9",
          background: "#fff",
        }}>
          <div style={{ width: 82, fontSize: 12, fontWeight: 700, color: "#475569" }}>
            {timeLabel(r.start_time) || "—"}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {r.staff_name}
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              {r.class_name}
            </div>
          </div>
          {r.submitted ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ background: "#f1f5f9", borderRadius: 4, width: 80, height: 6 }}>
                <div style={{
                  width: `${r.attendance_rate}%`, height: 6, borderRadius: 4,
                  background: rateColor(r.attendance_rate),
                }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 800, color: rateColor(r.attendance_rate), minWidth: 34, textAlign: "right" }}>
                {r.attendance_rate}%
              </span>
            </div>
          ) : (
            <span style={{
              fontSize: 10.5, fontWeight: 700, color: "#92400e",
              background: "#fef3c7", border: "1px solid #fde68a",
              borderRadius: 6, padding: "2px 8px", textTransform: "uppercase", letterSpacing: 0.4,
            }}>Awaiting attendance</span>
          )}
        </div>
      ))}
      {sorted.length > displayed.length && (
        <div style={{ fontSize: 11, color: "#94a3b8", padding: "4px 6px" }}>
          Showing first {displayed.length} of {sorted.length} sessions · see full list in the table below.
        </div>
      )}
    </div>
  );
};

const EmptyState: React.FC<{ label?: string }> = ({ label }) => (
  <div style={{
    height: 200, display: "flex", alignItems: "center",
    justifyContent: "center", color: "#94a3b8", fontSize: 13,
  }}>
    {label || "No data for this period"}
  </div>
);

const HeatLegend: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <span style={{ width: 12, height: 12, background: color, borderRadius: 3, border: color === "#f1f5f9" ? "1px solid #e2e8f0" : "none" }} />
    {label}
  </span>
);

// GitHub-style contributions grid. Rows = students, columns = every session
// in the current filter (max 20 shown). Each cell = that student's status
// for that session, or grey when no attendance existed.
interface HeatmapProps {
  students: StudentBreakdownRow[];
  sessions: ReportRow[];
}
const Heatmap: React.FC<HeatmapProps> = ({ students, sessions }) => {
  // Cell + spacing constants — bumped for richer visual density.
  const CELL = 16;
  const GAP = 4;
  const NAME_COL = 180;   // avatar (38) + gap + name column
  const RATE_COL = 56;

  const visible = sessions.slice(-20);

  // Build a lookup: (studentId → date → status).
  const lookup: Record<number, Record<string, string>> = {};
  students.forEach((s) => {
    lookup[s.id] = {};
    (s.recent_sessions || []).forEach((r) => {
      if (r.date) lookup[s.id][r.date] = r.status;
    });
  });

  const cell = (status: string | undefined) => {
    if (status === "present") return "#16a34a";
    if (status === "late")    return "#f59e0b";
    if (status === "absent")  return "#ef4444";
    return "#f1f5f9";
  };

  // Precompute per-column labels: day number always, month label only on
  // the FIRST column of each month (visually anchors the timeline).
  const columns = visible.map((sess, i) => {
    const d = new Date(sess.date);
    const monthKey = `${d.getFullYear()}-${d.getMonth()}`;
    const prev = i > 0 ? new Date(visible[i - 1].date) : null;
    const prevKey = prev ? `${prev.getFullYear()}-${prev.getMonth()}` : "";
    return {
      sess,
      day: String(d.getDate()),
      monthLabel: monthKey !== prevKey
        ? d.toLocaleDateString("en-US", { month: "short" })
        : "",
    };
  });

  return (
    <div>
      {/* Column headers — month label (first-in-month only) then day numbers */}
      <div style={{ display: "flex", marginBottom: 6 }}>
        <div style={{ width: NAME_COL, flexShrink: 0 }} />
        <div style={{ flex: 1, display: "flex", gap: GAP, flexWrap: "nowrap" }}>
          {columns.map((c, i) => (
            <div key={i} style={{
              width: CELL, flexShrink: 0, textAlign: "center",
              fontSize: 9, color: "#1b3a6b", fontWeight: 700,
              lineHeight: 1, letterSpacing: "0.3px",
              textTransform: "uppercase", minHeight: 10,
            }}>
              {c.monthLabel}
            </div>
          ))}
        </div>
        <div style={{ width: RATE_COL, flexShrink: 0 }} />
      </div>
      <div style={{ display: "flex", marginBottom: 8 }}>
        <div style={{ width: NAME_COL, flexShrink: 0 }} />
        <div style={{ flex: 1, display: "flex", gap: GAP, flexWrap: "nowrap" }}>
          {columns.map((c, i) => (
            <div key={i} style={{
              width: CELL, flexShrink: 0, textAlign: "center",
              fontSize: 9, color: "#94a3b8", lineHeight: 1,
            }}>
              {c.day}
            </div>
          ))}
        </div>
        <div style={{ width: RATE_COL, flexShrink: 0 }} />
      </div>

      {/* Rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {students.map((s) => (
          <div key={s.id} style={{
            display: "flex", alignItems: "center",
            padding: "10px 0",
            borderTop: "1px solid #f8fafc",
          }}>
            <div style={{
              width: NAME_COL, flexShrink: 0,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <img
                src={getAvatar(s.name, s.avatar, 38)}
                alt=""
                style={{
                  width: 38, height: 38, borderRadius: "50%",
                  objectFit: "cover", border: "1.5px solid #e2e8f0", flexShrink: 0,
                }}
              />
              <div style={{
                fontSize: 14, fontWeight: 700, color: "#1a1a2e",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                minWidth: 0,
              }}>{s.name}</div>
            </div>

            <div style={{ flex: 1, display: "flex", gap: GAP, flexWrap: "nowrap" }}>
              {columns.map(({ sess }) => {
                const status = lookup[s.id]?.[sess.date];
                const bg = cell(status);
                const tip = status
                  ? `${s.name} · ${new Date(sess.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · ${status[0].toUpperCase()}${status.slice(1)} · ${sess.class_name}`
                  : `${s.name} · ${new Date(sess.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })} · No class`;
                return (
                  <div
                    key={sess.session_id}
                    title={tip}
                    style={{
                      width: CELL, height: CELL, borderRadius: 4,
                      background: bg,
                      border: bg === "#f1f5f9" ? "1px solid #e2e8f0" : "none",
                      flexShrink: 0, cursor: "default",
                    }}
                  />
                );
              })}
            </div>

            <div style={{
              width: RATE_COL, textAlign: "right",
              fontWeight: 800, fontSize: 16,
              color: s.rate >= 80 ? "#16a34a" : s.rate >= 50 ? "#d97706" : "#dc2626",
            }}>{s.rate}%</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SectionDivider: React.FC = () => (
  <div style={{ height: 1, background: "#f1f5f9", margin: "4px 20px" }} />
);

const PanelSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ padding: "14px 20px" }}>
    <div style={{
      fontSize: 11, fontWeight: 700, color: "#1b3a6b",
      textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 10,
    }}>{title}</div>
    {children}
  </div>
);

const MutedRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>{children}</div>
);

const rowStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 10, padding: "8px 0",
};

export default AttendanceReport;
